import { useState, useMemo } from 'react';
import { Wallet, LogOut, ChevronDown, ExternalLink } from 'lucide-react';
import { useWallet } from '@/hooks/useWallet';
import { cn, truncateAddress } from '@/lib/utils';
import { Button } from './Button';

export function WalletButton() {
  const {
    address,
    isConnected,
    isConnecting,
    connect,
    disconnect,
    connectors,
    connectorName,
  } = useWallet();

  const [showConnectors, setShowConnectors] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  // Sort connectors: detected (installed) first, then not detected
  const sortedConnectors = useMemo(() => {
    const detected: typeof connectors = [];
    const notDetected: typeof connectors = [];

    connectors.forEach(connector => {
      if (connector.available()) {
        detected.push(connector);
      } else {
        notDetected.push(connector);
      }
    });

    return [...detected, ...notDetected];
  }, [connectors]);

  if (isConnected && address) {
    return (
      <div className="relative">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-xl',
            'bg-secondary/50 border border-border/20',
            'hover:border-primary/20 transition-all'
          )}
        >
          <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="font-mono text-sm">{truncateAddress(address)}</span>
          <ChevronDown className={cn(
            'w-4 h-4 transition-transform',
            showMenu && 'rotate-180'
          )} />
        </button>

        {showMenu && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowMenu(false)}
            />
            <div className="absolute right-0 mt-2 w-56 z-50 bg-card/90 backdrop-blur-xl border border-border/10 rounded-xl shadow-xl overflow-hidden animate-fade-in">
              <div className="p-3 border-b border-border/10">
                <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">Connected with {connectorName}</p>
                <p className="font-mono text-sm mt-1">{truncateAddress(address, 10, 8)}</p>
              </div>

              <div className="p-2">
                <a
                  href={`https://sepolia.voyager.online/contract/${address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-primary/[0.05] transition-colors text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  View on Explorer
                </a>

                <button
                  onClick={() => {
                    disconnect();
                    setShowMenu(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-destructive/10 transition-colors text-sm text-destructive w-full"
                >
                  <LogOut className="w-4 h-4" />
                  Disconnect
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <Button
        onClick={() => setShowConnectors(!showConnectors)}
        loading={isConnecting}
        className="gap-2"
      >
        <Wallet className="w-4 h-4" />
        Connect Wallet
      </Button>

      {showConnectors && !isConnecting && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowConnectors(false)}
          />
          <div className="absolute right-0 mt-2 w-72 z-50 overflow-hidden animate-fade-in rounded-2xl">
            {/* Gradient border */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-accent-violet/20 to-transparent rounded-2xl" />
            <div className="absolute inset-[1px] bg-card/95 backdrop-blur-xl rounded-2xl" />
            
            <div className="relative z-10">
              <div className="p-4 border-b border-border/10">
                <p className="font-semibold text-lg">Connect a wallet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose your preferred wallet
                </p>
              </div>

              <div className="p-2 space-y-1 max-h-80 overflow-y-auto">
                {sortedConnectors.map((connector) => {
                  const isDetected = connector.available();

                  return (
                    <button
                      key={connector.id}
                      onClick={() => {
                        connect({ connector });
                        setShowConnectors(false);
                      }}
                      className={cn(
                        "flex items-center gap-3 w-full px-3 py-3 rounded-xl transition-colors",
                        "hover:bg-primary/[0.05]"
                      )}
                    >
                      {connector.icon ? (
                        <img
                          src={typeof connector.icon === 'string' ? connector.icon : connector.icon.dark}
                          alt=""
                          className="w-10 h-10 rounded-xl"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-secondary/50 flex items-center justify-center">
                          <Wallet className="w-5 h-5 text-muted-foreground" />
                        </div>
                      )}
                      <span className="font-medium flex-1 text-left">{connector.name}</span>
                      {isDetected && (
                        <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-success">
                          Detected
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
